/**
 * Active company state — F9 T9.1 (slice 1D) + tryb "Wszystkie firmy" (all-companies).
 *
 * Persisted w `localStorage.active_company_id`. Read/write z fallback chain:
 *   1) localStorage === ALL_COMPANIES sentinel (gdy >= 2 firmy) -> tryb "Wszystkie"
 *   2) localStorage (jezeli w `me.companies`)
 *   3) `me.activeCompanyId` (backend resolved)
 *   4) primary company z `me.companies`
 *   5) pierwsza alfabetycznie z `me.companies`
 *
 * Defensywnie: jezeli `localStorage` zawiera id ktorego nie ma w `me.companies`
 * (firma skasowana / utracone UCA), fallback chain wraca do innej firmy. Analogicznie
 * sentinel ALL_COMPANIES jest ignorowany gdy user ma < 2 firmy (auto-collapse do
 * jedynej firmy / null) — nie ma sensu "Wszystkie" gdy jest tylko jedna do wyboru.
 *
 * Zapis: prosty `setItem` + synchroniczne powiadomienie subskrybentow (pub/sub, mirror
 * `lib/access-token.ts`) — axios interceptor czyta przy kazdym request, UI (CompanySwitcher,
 * `useActiveCompanyScope`) re-renderuje sie natychmiast po zmianie z innego miejsca w drzewie.
 */

import type { TenantCompany } from '@/contexts/TenantAuthContext';

const STORAGE_KEY = 'active_company_id';

/** Sentinel przechowywany w localStorage gdy user wybral tryb "Wszystkie firmy". */
export const ALL_COMPANIES = 'all' as const;

let subscribers: Array<() => void> = [];

/**
 * Zapisuje callback wywolywany przy kazdej zmianie aktywnej firmy (setStoredActiveCompanyId).
 * Zwraca `unsubscribe`. Uzywane przez `useActiveCompanyScope` (useSyncExternalStore) zeby
 * UI re-renderowalo sie natychmiast po kliknieciu w CompanySwitcher, bez czekania na kolejny
 * fetch `/me`.
 */
export function subscribeActiveCompany(cb: () => void): () => void {
  subscribers.push(cb);
  return () => {
    subscribers = subscribers.filter((s) => s !== cb);
  };
}

function notifyActiveCompanySubscribers(): void {
  for (const cb of subscribers) cb();
}

export function getStoredActiveCompanyId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredActiveCompanyId(id: string | null): void {
  if (typeof window !== 'undefined') {
    try {
      if (id) {
        window.localStorage.setItem(STORAGE_KEY, id);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage moze byc disabled (private mode, quota) — fail silent
    }
  }
  notifyActiveCompanySubscribers();
}

/**
 * Resolve aktywnej firmy z fallback chain. Zwraca id (string), sentinel `ALL_COMPANIES`,
 * lub null gdy user nie ma zadnych firm.
 */
export function resolveActiveCompanyId(
  companies: TenantCompany[],
  backendActiveId: string | null,
): string | null {
  if (companies.length === 0) return null;

  const validIds = new Set(companies.map((c) => c.id));
  const stored = getStoredActiveCompanyId();

  // 1) sentinel "Wszystkie firmy" — honorowany tylko gdy jest z czego wybierac (>= 2 firmy).
  //    Ponizej 2 firm ignorujemy sentinel i spadamy dalej w lancuchu (auto-collapse).
  if (stored === ALL_COMPANIES) {
    if (companies.length >= 2) return ALL_COMPANIES;
  } else if (stored && validIds.has(stored)) {
    // 2) localStorage (gdy nadal w companies)
    return stored;
  }

  // 3) backend activeCompanyId
  if (backendActiveId && validIds.has(backendActiveId)) return backendActiveId;

  // 4) primary
  const primary = companies.find((c) => c.isPrimary);
  if (primary) return primary.id;

  // 5) pierwsza alfabetycznie
  const sorted = [...companies].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  return sorted[0]?.id ?? null;
}

/** Wynik resolve — ksztalt wygodny do rozgalezienia w UI/API-client bez porownan stringowych. */
export type ActiveCompanyScope =
  | { mode: 'all' }
  | { mode: 'company'; companyId: string }
  | { mode: 'none' };

/**
 * Jak `resolveActiveCompanyId`, ale zwraca zdyskryminowana unie zamiast surowego
 * stringa/sentinela — callerzy (UI, `useActiveCompanyScope`) nie musza znac/porownywac
 * `ALL_COMPANIES` recznie.
 */
export function resolveActiveCompanyScope(
  companies: TenantCompany[],
  backendActiveId: string | null,
): ActiveCompanyScope {
  const resolved = resolveActiveCompanyId(companies, backendActiveId);
  if (resolved === null) return { mode: 'none' };
  if (resolved === ALL_COMPANIES) return { mode: 'all' };
  return { mode: 'company', companyId: resolved };
}
